import { useState } from "react";
import { toast } from "sonner";
import { downloadFile, errText } from "../../lib/api";
import { useAuditLogs } from "../../hooks/useAuditLogs";

const ENTITY_TYPES = [
  ["", "All activity"], ["employee", "Employees"], ["attendance", "Attendance"], ["payroll", "Payroll runs"],
  ["adjustment", "Adjustments"], ["loan", "Loans"], ["leave_request", "Leave"],
  ["regularization", "Regularizations"], ["company_document", "Documents"], ["system", "System"],
];

const ACTION_STYLES = {
  created: "bg-green-100 text-green-800", uploaded: "bg-green-100 text-green-800",
  approved: "bg-green-100 text-green-800", run: "bg-blue-100 text-blue-800",
  deleted: "bg-red-100 text-red-700", rejected: "bg-red-100 text-red-700",
  purged: "bg-red-100 text-red-700", role_changed: "bg-purple-100 text-purple-800",
  changed: "bg-amber-100 text-amber-800", reset: "bg-amber-100 text-amber-800",
};

const actionStyle = (action) => ACTION_STYLES[action.split(".").pop()] || "bg-stone-100 text-stone-600";

const fmtTime = (iso) =>
  new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const DetailChips = ({ details }) => {
  const entries = Object.entries(details || {});
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {entries.map(([k, v]) => (
        <span key={k} className="px-2 py-0.5 rounded bg-stone-100 text-[11px] text-stone-600 tabular-nums">
          {k}: {v && typeof v === "object" && ("from" in v || "to" in v)
            ? `${fmtVal(v.from)} → ${fmtVal(v.to)}`
            : fmtVal(v)}
        </span>
      ))}
    </div>
  );
};

export default function AuditLog() {
  const [entityType, setEntityType] = useState("");
  const [search, setSearch] = useState("");
  const { logs, total, forbidden, loadMore } = useAuditLogs(entityType, search);

  if (forbidden) {
    return <p className="p-8 text-center text-sm text-stone-500" data-testid="audit-forbidden">Only a Super Admin can view the audit trail.</p>;
  }

  return (
    <div className="space-y-6" data-testid="admin-audit-log-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Audit trail</h1>
        <p className="text-sm text-stone-500 mt-1">Who did what — role changes, salary edits, payroll runs, approvals and deletions.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={entityType} onChange={(e) => setEntityType(e.target.value)} data-testid="audit-entity-filter"
          className="min-h-[44px] px-3 rounded-lg border border-stone-300 bg-white text-sm">
          {ENTITY_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search person or record…"
          data-testid="audit-search-input"
          className="min-h-[44px] px-4 rounded-lg border border-stone-300 bg-white text-sm flex-1 min-w-[200px]" />
        <button onClick={() => {
          const params = new URLSearchParams();
          if (entityType) params.set("entity_type", entityType);
          if (search) params.set("search", search);
          const qs = params.toString();
          downloadFile(`/audit-logs/export${qs ? `?${qs}` : ""}`, `audit_trail_${new Date().toISOString().slice(0, 10)}.csv`)
            .then(() => toast.success("Audit trail downloaded"))
            .catch((e) => toast.error(errText(e)));
        }} data-testid="audit-export-button"
          className="tap-scale min-h-[44px] px-5 rounded-full bg-secondary text-white text-sm font-bold hover:opacity-90 transition-opacity duration-200">
          Download CSV
        </button>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
        {logs.length === 0 && <p className="p-10 text-center text-sm text-stone-400" data-testid="audit-empty">No audit entries yet. Actions like role changes, payroll runs and approvals will appear here.</p>}
        {logs.map((l) => (
          <div key={l.id} className="p-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4" data-testid="audit-log-row">
            <div className="sm:w-44 shrink-0 text-xs text-stone-500 tabular-nums pt-0.5">{fmtTime(l.timestamp)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-sm">{l.actor_name}</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${actionStyle(l.action)}`}>{l.action}</span>
                {l.entity_name && <span className="text-sm text-stone-600 truncate">{l.entity_name}</span>}
              </div>
              <DetailChips details={l.details} />
            </div>
          </div>
        ))}
      </div>

      {logs.length < total && (
        <button onClick={loadMore} data-testid="audit-load-more"
          className="tap-scale w-full min-h-[48px] rounded-full bg-white border border-stone-300 text-sm font-bold text-stone-700 hover:bg-stone-50 transition-colors duration-200">
          Load more ({total - logs.length} older)
        </button>
      )}
    </div>
  );
}
